import React, { useEffect, useState } from 'react';
import Icon from '@mdi/react';
import { mdiDocker } from '@mdi/js';

import {
    Box,
    Typography,
    Paper,
    Button,
    Grid,
    Card,
    CardContent,
    CardActions,
    IconButton,
    Chip,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Switch,
    FormControlLabel,
    Tooltip,
    Avatar,
    Container,
    Fade,
    useTheme,
    alpha,
    Tabs,
    Tab,
    Divider,
    Collapse,
    Autocomplete,
    SvgIcon,
    SvgIconProps,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ContentCopy as CopyIcon,
    Settings as SettingsIcon,
    Close as CloseIcon,
    ExpandMore,
    ExpandLess,
    CheckCircle,
    Cancel,
    Storage,
    Preview as PreviewIcon,
    Save as SaveIcon,
} from '@mui/icons-material';
import { observer } from 'mobx-react-lite';
import { useStore } from '../store';
import { DeploymentTemplate, DeploymentTemplateCreateRequest } from '../types/deployment';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`template-tabpanel-${index}`}
            aria-labelledby={`template-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

function DockerIcon(props: SvgIconProps) {
    return (
        <SvgIcon {...props}>
            <path d={mdiDocker} />
        </SvgIcon>
    );
}

const DeploymentTemplates: React.FC = observer(() => {
    const { deploymentStore } = useStore();
    const theme = useTheme();
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<DeploymentTemplate | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [expandedVars, setExpandedVars] = useState(false);
    const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
    const [previewResult, setPreviewResult] = useState<any>(null);
    const [validationResult, setValidationResult] = useState<any>(null);

    const [formData, setFormData] = useState<DeploymentTemplateCreateRequest>({
        name: '',
        description: '',
        env_template: '',
        dockerfile: '',
        docker_compose: '',
        default_port: 8080,
        required_env_vars: [],
        tags: [],
        is_active: true,
    });

    const [envVarsPreview, setEnvVarsPreview] = useState<{ [key: string]: string }>({});

    const availableTags = ['nodejs', 'python', 'go', 'java', 'php', 'ruby', 'web', 'api', 'database', 'static'];

    const exampleTemplates = [
        {
            name: 'Node.js Express',
            description: 'Шаблон для Node.js приложений с Express',
            tags: ['nodejs', 'express', 'web'],
        },
        {
            name: 'Python Django',
            description: 'Шаблон для Python Django приложений',
            tags: ['python', 'django', 'web'],
        },
        {
            name: 'Go Fiber',
            description: 'Шаблон для Go приложений с Fiber',
            tags: ['go', 'fiber', 'api'],
        },
        {
            name: 'Static Website',
            description: 'Шаблон для статических сайтов с Nginx',
            tags: ['static', 'nginx', 'web'],
        },
    ];

    useEffect(() => {
        deploymentStore.fetchTemplates();
    }, [deploymentStore]);

    const handleOpenDialog = (template?: DeploymentTemplate) => {
        if (template) {
            setSelectedTemplate(template);
            setIsEditing(true);
            setFormData({
                name: template.name,
                description: template.description,
                env_template: template.env_template,
                dockerfile: template.dockerfile,
                docker_compose: template.docker_compose || '',
                default_port: template.default_port,
                required_env_vars: template.required_env_vars || [],
                tags: template.tags || [],
                is_active: template.is_active,
            });
        } else {
            setSelectedTemplate(null);
            setIsEditing(false);
            setFormData({
                name: '',
                description: '',
                env_template: '',
                dockerfile: '',
                docker_compose: '',
                default_port: 8080,
                required_env_vars: [],
                tags: [],
                is_active: true,
            });
        }
        setTabValue(0);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedTemplate(null);
        setValidationResult(null);
    };

    const handleSubmit = async () => {
        if (isEditing && selectedTemplate) {
            const success = await deploymentStore.updateTemplate(selectedTemplate.id, formData);
            if (success) {
                handleCloseDialog();
            }
        } else {
            const success = await deploymentStore.createTemplate(formData);
            if (success) {
                handleCloseDialog();
            }
        }
    };

    const handleDelete = async (id: number) => {
        if (window.confirm('Вы уверены, что хотите удалить этот шаблон?')) {
            await deploymentStore.deleteTemplate(id);
        }
    };

    const handleClone = async (id: number) => {
        await deploymentStore.cloneTemplate(id);
    };

    const handleValidate = async () => {
        const result = await deploymentStore.validateTemplate(formData);
        setValidationResult(result);
    };

    const handlePreview = async (template: DeploymentTemplate) => {
        const result = await deploymentStore.previewTemplate(template.id, envVarsPreview);
        if (result) {
            setPreviewResult(result);
            setOpenPreviewDialog(true);
        }
    };

    const extractVariables = (template: string): string[] => {
        const regex = /\{\{\.(\w+)\}\}/g;
        const variables = new Set<string>();
        let match;
        while ((match = regex.exec(template)) !== null) {
            variables.add(match[1]);
        }
        return Array.from(variables);
    };

    const getStatusIcon = (template: DeploymentTemplate) => {
        if (!template.is_active) return <Cancel color="error" />;
        return <CheckCircle color="success" />;
    };

    const TemplateCard = ({ template }: { template: DeploymentTemplate }) => {
        const [expanded, setExpanded] = useState(false);

        return (
            <Fade in timeout={300}>
                <Card
                    sx={{
                        height: '100%',
                        transition: 'all 0.3s ease',
                        border: '1px solid',
                        borderColor: template.is_active ? 'divider' : alpha(theme.palette.error.main, 0.3),
                        opacity: template.is_active ? 1 : 0.8,
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: 6,
                        },
                    }}
                >
                    <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                            <Box display="flex" alignItems="center" gap={2}>
                                <Avatar
                                    sx={{
                                        bgcolor: template.is_active ? 'primary.main' : 'grey.500',
                                        width: 48,
                                        height: 48,
                                    }}
                                >
                                    <DockerIcon/>
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" fontWeight={700}>
                                        {template.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {template.description}
                                    </Typography>
                                </Box>
                            </Box>
                            {getStatusIcon(template)}
                        </Box>

                        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                            {template.tags?.map((tag) => (
                                <Chip
                                    key={tag}
                                    label={tag}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                />
                            ))}
                        </Box>

                        <Box display="flex" alignItems="center" gap={2} mb={1}>
                            <Chip
                                icon={<Storage />}
                                label={`Порт: ${template.default_port}`}
                                size="small"
                            />
                            {template.required_env_vars && template.required_env_vars.length > 0 && (
                                <Chip
                                    icon={<SettingsIcon />}
                                    label={`${template.required_env_vars.length} переменных`}
                                    size="small"
                                    onClick={() => setExpanded(!expanded)}
                                    onDelete={() => setExpanded(!expanded)}
                                    deleteIcon={expanded ? <ExpandLess /> : <ExpandMore />}
                                />
                            )}
                        </Box>

                        <Collapse in={expanded}>
                            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary" gutterBottom>
                                    Обязательные переменные:
                                </Typography>
                                <Box display="flex" gap={0.5} flexWrap="wrap" mt={1}>
                                    {template.required_env_vars?.map((varName) => (
                                        <Chip
                                            key={varName}
                                            label={`{{.${varName}}}`}
                                            size="small"
                                            variant="filled"
                                            sx={{ fontFamily: 'monospace' }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        </Collapse>
                    </CardContent>

                    <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                        <Box>
                            <Tooltip title="Предпросмотр">
                                <IconButton
                                    size="small"
                                    onClick={() => handlePreview(template)}
                                    color="primary"
                                >
                                    <PreviewIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Клонировать">
                                <IconButton
                                    size="small"
                                    onClick={() => handleClone(template.id)}
                                    color="primary"
                                >
                                    <CopyIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Box>
                            <Tooltip title="Редактировать">
                                <IconButton
                                    size="small"
                                    onClick={() => handleOpenDialog(template)}
                                    color="primary"
                                >
                                    <EditIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Удалить">
                                <IconButton
                                    size="small"
                                    onClick={() => handleDelete(template.id)}
                                    color="error"
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </CardActions>
                </Card>
            </Fade>
        );
    };

    if (deploymentStore.loading) {
        return (
            <Container maxWidth="xl">
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px">
                    <CircularProgress size={60} thickness={4} />
                    <Typography variant="h6" color="textSecondary" sx={{ mt: 3 }}>
                        Загрузка шаблонов...
                    </Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl">
            <Box mb={4}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Box>
                        <Typography variant="h3" gutterBottom fontWeight="bold" color="primary">
                            Шаблоны развертывания
                        </Typography>
                        <Typography variant="h6" color="textSecondary">
                            Управление шаблонами для автоматического развертывания приложений
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                        size="large"
                    >
                        Создать шаблон
                    </Button>
                </Box>

                {deploymentStore.error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {deploymentStore.error}
                    </Alert>
                )}

                {/* Статистика */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="h4" color="primary">
                                {deploymentStore.templates.length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Всего шаблонов
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="h4" color="success.main">
                                {deploymentStore.activeTemplates.length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Активных
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="h4" color="info.main">
                                {deploymentStore.templates.filter(t => t.tags?.includes('web')).length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Web приложений
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="h4" color="warning.main">
                                {deploymentStore.templates.filter(t => t.tags?.includes('api')).length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                API сервисов
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Список шаблонов */}
                {deploymentStore.templates.length > 0 ? (
                    <Grid container spacing={3}>
                        {deploymentStore.templates.map((template) => (
                            <Grid item xs={12} sm={6} md={4} key={template.id}>
                                <TemplateCard template={template} />
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <Paper sx={{ p: 8, textAlign: 'center' }}>
                        <DockerIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 3, opacity: 0.5 }} />
                        <Typography variant="h5" color="textSecondary" gutterBottom>
                            Нет созданных шаблонов
                        </Typography>
                        <Typography variant="body1" color="textSecondary" paragraph>
                            Создайте первый шаблон для автоматического развертывания приложений
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenDialog()}
                            size="large"
                        >
                            Создать шаблон
                        </Button>
                    </Paper>
                )}
            </Box>

            {/* Диалог создания/редактирования */}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: { height: '90vh', display: 'flex', flexDirection: 'column' }
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">
                            {isEditing ? 'Редактировать шаблон' : 'Создать новый шаблон'}
                        </Typography>
                        <IconButton onClick={handleCloseDialog}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <Divider />

                <DialogContent sx={{ flex: 1, overflow: 'auto' }}>
                    <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
                        <Tab label="Основная информация" />
                        <Tab label="Dockerfile" />
                        <Tab label="Переменные окружения" />
                        <Tab label="Docker Compose" />
                    </Tabs>

                    <TabPanel value={tabValue} index={0}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Название шаблона"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Описание"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    multiline
                                    rows={2}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Порт по умолчанию"
                                    type="number"
                                    value={formData.default_port}
                                    onChange={(e) => setFormData({ ...formData, default_port: parseInt(e.target.value) || 8080 })}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    multiple
                                    options={availableTags}
                                    value={formData.tags || []}
                                    onChange={(_, newValue) => setFormData({ ...formData, tags: newValue })}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Теги" placeholder="Выберите теги" />
                                    )}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                                        ))
                                    }
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.is_active !== false}
                                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                        />
                                    }
                                    label="Активный шаблон"
                                />
                            </Grid>

                            {/* Примеры шаблонов */}
                            {!isEditing && (
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Использовать пример:
                                    </Typography>
                                    <Box display="flex" gap={1} flexWrap="wrap">
                                        {exampleTemplates.map((example) => (
                                            <Chip
                                                key={example.name}
                                                label={example.name}
                                                onClick={() => {
                                                    setFormData({
                                                        ...formData,
                                                        name: example.name,
                                                        description: example.description,
                                                        tags: example.tags,
                                                    });
                                                }}
                                                color="primary"
                                                variant="outlined"
                                            />
                                        ))}
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        <TextField
                            fullWidth
                            label="Dockerfile"
                            value={formData.dockerfile}
                            onChange={(e) => setFormData({ ...formData, dockerfile: e.target.value })}
                            multiline
                            rows={20}
                            sx={{
                                '& .MuiInputBase-input': {
                                    fontFamily: 'monospace',
                                    fontSize: '14px',
                                },
                            }}
                            placeholder={`FROM node:18-alpine

WORKDIR /app

COPY package*.json ./
RUN npm ci --only=production

COPY . .

EXPOSE {{.Port}}

CMD ["node", "server.js"]`}
                        />
                    </TabPanel>

                    <TabPanel value={tabValue} index={2}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Шаблон .env файла"
                                    value={formData.env_template}
                                    onChange={(e) => setFormData({ ...formData, env_template: e.target.value })}
                                    multiline
                                    rows={15}
                                    sx={{
                                        '& .MuiInputBase-input': {
                                            fontFamily: 'monospace',
                                            fontSize: '14px',
                                        },
                                    }}
                                    placeholder={`# Application
NODE_ENV={{.NodeEnv}}
PORT={{.Port}}

# Database
DB_HOST={{.DatabaseHost}}
DB_PORT={{.DatabasePort}}
DB_USER={{.DatabaseUser}}
DB_PASSWORD={{.DatabasePassword}}
DB_NAME={{.DatabaseName}}

# JWT
JWT_SECRET={{.JWTSecret}}`}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Обязательные переменные окружения:
                                </Typography>
                                <Autocomplete
                                    multiple
                                    freeSolo
                                    options={extractVariables(formData.env_template)}
                                    value={formData.required_env_vars || []}
                                    onChange={(_, newValue) => setFormData({ ...formData, required_env_vars: newValue })}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Добавьте обязательные переменные"
                                            helperText="Эти переменные должны быть заполнены при развертывании"
                                        />
                                    )}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip
                                                variant="outlined"
                                                label={option}
                                                {...getTagProps({ index })}
                                                sx={{ fontFamily: 'monospace' }}
                                            />
                                        ))
                                    }
                                />
                            </Grid>
                        </Grid>
                    </TabPanel>

                    <TabPanel value={tabValue} index={3}>
                        <TextField
                            fullWidth
                            label="docker-compose.yml (опционально)"
                            value={formData.docker_compose || ''}
                            onChange={(e) => setFormData({ ...formData, docker_compose: e.target.value })}
                            multiline
                            rows={20}
                            sx={{
                                '& .MuiInputBase-input': {
                                    fontFamily: 'monospace',
                                    fontSize: '14px',
                                },
                            }}
                            placeholder={`version: '3.8'
services:
  app:
    build: .
    container_name: {{.ContainerName}}
    ports:
      - "{{.Port}}:{{.Port}}"
    environment:
      - NODE_ENV={{.NodeEnv}}
    networks:
      - app-network

networks:
  app-network:
    driver: bridge`}
                        />
                    </TabPanel>

                    {validationResult && (
                        <Alert severity={validationResult.valid ? 'success' : 'error'} sx={{ mt: 2 }}>
                            {validationResult.valid ? 'Шаблон валиден' : 'Обнаружены ошибки в шаблоне'}
                            {validationResult.errors && Object.entries(validationResult.errors).map(([field, errors]) => (
                                <Box key={field}>
                                    <strong>{field}:</strong> {(errors as string[]).join(', ')}
                                </Box>
                            ))}
                        </Alert>
                    )}
                </DialogContent>

                <Divider />

                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleValidate} startIcon={<CheckCircle />}>
                        Проверить
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    <Button onClick={handleCloseDialog}>Отмена</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={deploymentStore.saving || !formData.name || !formData.dockerfile || !formData.env_template}
                        startIcon={deploymentStore.saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    >
                        {deploymentStore.saving ? 'Сохранение...' : (isEditing ? 'Сохранить' : 'Создать')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог предпросмотра */}
            <Dialog
                open={openPreviewDialog}
                onClose={() => setOpenPreviewDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Предпросмотр конфигурации</Typography>
                        <IconButton onClick={() => setOpenPreviewDialog(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {previewResult && (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Контейнер: {previewResult.container_name}
                                </Typography>
                                <Typography variant="subtitle2" gutterBottom>
                                    Образ: {previewResult.image_name}
                                </Typography>
                                <Typography variant="subtitle2" gutterBottom>
                                    Порт: {previewResult.port}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Содержимое .env:
                                </Typography>
                                <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                                    <pre style={{ margin: 0, fontSize: '12px', overflow: 'auto' }}>
                                        {previewResult.env_content}
                                    </pre>
                                </Paper>
                            </Grid>
                            {previewResult.dockerfile && (
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Dockerfile:
                                    </Typography>
                                    <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                                        <pre style={{ margin: 0, fontSize: '12px', overflow: 'auto' }}>
                                            {previewResult.dockerfile}
                                        </pre>
                                    </Paper>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenPreviewDialog(false)}>Закрыть</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
});

export default DeploymentTemplates;